import { useCallback, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { addPageWidget, deletePageWidget, updatePageWidgetData, updatePageWidgets, updateSiteContentRequest, selectPlainSiteContent } from '../redux/slices/siteContentSlice';
import { toggleEditMode } from '../redux/slices/editModeSlice';
import { useSelector } from 'react-redux';
import { WidgetType } from '../widgets/widgetRegistry';
import { createDefaultWidgetData } from '../utils/widgetDefaults';

export interface PageWidget {
  id?: string;
  type: WidgetType;
  data: any;
  index: number;
}

export const useDynamicPageWidgets = (pageType: string) => {
  const siteContent = useSelector(selectPlainSiteContent);
  const { isEditMode } = useAppSelector((state) => state.editMode);
  const { updateLoading, error } = useAppSelector((state) => state.siteContent);
  const dispatch = useAppDispatch();
  
  const [localWidgets, setLocalWidgets] = useState<PageWidget[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Check if page exists in site content
  const pageExists = !!siteContent?.pages?.[pageType];

  // Sync local widgets from Redux - ONLY from pages structure
  useEffect(() => {
    const pageWidgets = siteContent?.pages?.[pageType]?.widgets || [];

    const mappedWidgets: PageWidget[] = pageWidgets.map((widget: any, index: number) => ({
      id: widget.id,  // Include widget ID
      type: widget.type as WidgetType,
      data: widget.data,
      index: index  // Use page-level index
    }));

    setLocalWidgets(mappedWidgets);
  }, [siteContent, pageType]);

  // Reset saving state when loading completes
  useEffect(() => {
    if (!updateLoading && isSaving) {
      setIsSaving(false);
    }
  }, [updateLoading, isSaving]);

  // Update widget data in Redux (local state syncs via useEffect)
  const updateWidgetData = useCallback((widgetIndex: number, newData: any) => {
    dispatch(updatePageWidgetData({ 
      slug: pageType, 
      index: widgetIndex, 
      data: newData 
    }));
  }, [dispatch, pageType]);

  // Save all widget data changes to Redux and backend
  const saveWidgets = useCallback(async () => {
    setIsSaving(true);
    
    // Update page-specific widgets array
    const pageWidgets = localWidgets.map(widget => ({
      id: widget.id,  // Preserve widget ID
      type: widget.type,
      data: widget.data
    }));
    dispatch(updatePageWidgets({ slug: pageType, widgets: pageWidgets }));

    // Save to backend
    dispatch(updateSiteContentRequest());
  }, [dispatch, localWidgets, pageType]);

  // Toggle edit mode (save when closing edit mode)
  const toggleEditModeWithSave = useCallback(async () => {
    if (isEditMode) {
      await saveWidgets();
    }
    dispatch(toggleEditMode());
  }, [dispatch, isEditMode, saveWidgets]);

  // Add new widget to page (after specific index)
  const addWidget = useCallback((widgetType: WidgetType, widgetData: any, afterIndex?: number) => {
    // Generate widget data if not provided
    const finalWidgetData = widgetData || createDefaultWidgetData(widgetType);
    
    const newWidget = { 
      id: crypto.randomUUID(),  // Unique identifier for each widget
      type: widgetType,  // Keep original widget type unchanged
      data: finalWidgetData 
    };
    
    dispatch(addPageWidget({ slug: pageType, widget: newWidget, afterIndex }));
    // API call will happen when user clicks Save button
  }, [dispatch, pageType]);

  // Delete widget from page using page-level index
  const deleteWidget = useCallback((widgetIndex: number) => {
    dispatch(deletePageWidget({ slug: pageType, index: widgetIndex }));
    // API call will happen when user clicks Save button
  }, [dispatch, pageType]);

  // Add multiple widgets in batch (all to Redux, then save once)
  const addMultipleWidgets = useCallback((widgetTypes: WidgetType[], afterIndex?: number) => {
    let currentAfterIndex = afterIndex;
    
    widgetTypes.forEach((widgetType) => {
      const finalWidgetData = createDefaultWidgetData(widgetType);
      
      const newWidget = { 
        id: crypto.randomUUID(),
        type: widgetType,
        data: finalWidgetData 
      };
      
      // Add to Redux state (synchronous)
      dispatch(addPageWidget({ 
        slug: pageType, 
        widget: newWidget, 
        afterIndex: currentAfterIndex 
      }));
      
      // Increment index for next widget (so they stack sequentially)
      if (currentAfterIndex !== undefined) {
        currentAfterIndex++;
      }
    });
    // Note: API save happens in parent component after this returns
  }, [dispatch, pageType]);

  return {
    widgets: localWidgets,
    updateWidgetData,
    saveWidgets,
    toggleEditModeWithSave,
    addWidget,
    addMultipleWidgets,
    deleteWidget,
    isEditMode,
    isSaving,
    hasError: !!error,
    hasWidgets: localWidgets.length > 0,
    pageExists
  };
};
